import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  ShieldCheck, Search, RefreshCw, Loader2, AlertTriangle, ChevronDown,
  ChevronRight, History,
} from 'lucide-react';
import { Button, Pill } from '../index';
import { ccGetControlEvidence } from '../../../services/api';

/**
 * P0-1 Sprint 2 — ControlEvidencePanel
 *
 * <p>Dashboard surface for the per-control evidence rollup. Auditors
 * use this to answer "for framework X, list every control we're
 * enforcing today and when each one entered enforcement." Backed
 * by GET /api/v1/compliance-center/control-evidence which fans the
 * tenant's framework_binding rows out by their control_mappings.</p>
 *
 * <h3>Read-only</h3>
 * <p>This is a dashboard, not an editor. To change a control's
 * status the operator follows the existing FrameworkBinding lifecycle
 * via PolicyConfigPageV2's per-binding actions; the panel here just
 * shows the resulting evidence.</p>
 *
 * <h3>Layout</h3>
 * <ul>
 *   <li>Top toolbar — framework filter, control-code search, date
 *       range, Reload button, total-rows pill.</li>
 *   <li>Grouped list — rows grouped by frameworkCode, each header
 *       collapsible. One row per (binding, control) pair.</li>
 *   <li>Per-row expand — shows the transition history embedded in
 *       the row (status changes, actor, timestamps).</li>
 * </ul>
 *
 * <h3>Props</h3>
 * <ul>
 *   <li><code>initialFrameworkCode</code> — optional, pre-applies
 *       the framework filter (e.g. when this panel is reached from
 *       a framework-specific deep-link).</li>
 * </ul>
 */
export function ControlEvidencePanel({ initialFrameworkCode } = {}) {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | error
  const [error, setError] = useState(null);
  const [filterFramework, setFilterFramework] = useState(initialFrameworkCode || '');
  const [filterControl, setFilterControl] = useState('');
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    const r = await ccGetControlEvidence({
      frameworkCode: filterFramework || undefined,
      controlCode:   filterControl   || undefined,
    });
    if (r?.ok === false) {
      setState('error');
      setError(r.error || 'load failed');
      return;
    }
    setRows(Array.isArray(r) ? r : (r?.data || []));
    setState('ready');
  }, [filterFramework, filterControl]);

  useEffect(() => { load(); }, [load]);

  /* Group by frameworkCode. Within each framework keep the rows in
   * the server's emit order (deterministic by binding iteration). */
  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      const fw = row.frameworkCode || 'UNKNOWN';
      if (!map.has(fw)) map.set(fw, []);
      map.get(fw).push(row);
    }
    return Array.from(map.entries()); // [[fwCode, rows], ...]
  }, [rows]);

  const toggleGroup = (fwCode) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(fwCode)) next.delete(fwCode);
      else next.add(fwCode);
      return next;
    });
  };

  const rowKey = (r) => `${r.bindingId}/${r.controlCode}`;
  const toggleExpand = (r) => {
    const k = rowKey(r);
    setExpandedRowKey((prev) => prev === k ? null : k);
  };

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <ShieldCheck size={14} className="text-v2-action-strong" />
        <h3 className="text-[13px] font-semibold text-v2-ink m-0">
          Per-control evidence
        </h3>
        {state === 'ready' && (
          <Pill tone="muted" size="sm">{rows.length} controls</Pill>
        )}
        <Button
          variant="ghost" size="sm"
          iconLeft={state === 'loading'
            ? <Loader2 size={12} className="animate-spin" />
            : <RefreshCw size={12} />}
          onClick={load}
          disabled={state === 'loading'}
          className="ml-auto"
        >
          Reload
        </Button>
      </div>

      <p className="text-[12px] text-v2-ink-secondary m-0 mb-3">
        One row per (binding, control) pair. Read-only — to advance a
        binding's state, use the binding's lifecycle actions in
        Policy Config. The transition history under each row is the
        tamper-evident chain from{' '}
        <code className="font-mono-v2 text-[11px]">framework_binding_transitions</code>.
      </p>

      <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: '1fr 1fr auto' }}>
        <label className="block">
          <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
            Framework
          </span>
          <input
            type="text"
            value={filterFramework}
            onChange={(e) => setFilterFramework(e.target.value)}
            placeholder="EU_AI_ACT (exact match, optional)"
            className="block w-full text-[12px] px-2 py-1.5 border border-v2-border rounded-v2-sm font-mono-v2"
          />
        </label>
        <label className="block">
          <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
            Control code search
          </span>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-2 text-v2-ink-muted" />
            <input
              type="text"
              value={filterControl}
              onChange={(e) => setFilterControl(e.target.value)}
              placeholder="ART-15 (substring, case-insensitive)"
              className="block w-full text-[12px] pl-7 pr-2 py-1.5 border border-v2-border rounded-v2-sm font-mono-v2"
            />
          </div>
        </label>
        <div className="flex items-end">
          <Button
            variant="secondary" size="sm"
            onClick={load}
            disabled={state === 'loading'}
          >
            Apply
          </Button>
        </div>
      </div>

      {state === 'loading' && (
        <div className="flex items-center gap-2 text-[12px] text-v2-ink-muted py-4">
          <Loader2 size={13} className="animate-spin" /> Loading control evidence…
        </div>
      )}

      {state === 'error' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-v2-sm bg-v2-warning-soft border border-v2-warning/30 text-[11.5px] text-v2-warning-strong">
          <AlertTriangle size={13} /> Could not load: {error}
        </div>
      )}

      {state === 'ready' && grouped.length === 0 && (
        <p className="text-[12.5px] text-v2-ink-muted py-2">
          No controls in the current window for the selected filters.
          {filterFramework || filterControl
            ? ' Try widening the filters.'
            : ' Verify a tenant binding exists with a populated control_mappings array.'}
        </p>
      )}

      {state === 'ready' && grouped.length > 0 && (
        <div className="space-y-3">
          {grouped.map(([fwCode, fwRows]) => {
            const collapsed = collapsedGroups.has(fwCode);
            return (
              <div key={fwCode} className="border border-v2-border-subtle rounded-v2-sm">
                <button
                  type="button"
                  onClick={() => toggleGroup(fwCode)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left bg-v2-surface-muted/40 hover:bg-v2-surface-muted/70 transition-colors"
                >
                  {collapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                  <span className="font-semibold text-[12.5px] text-v2-ink">{fwCode}</span>
                  <Pill tone="cyan" size="sm">{fwRows.length}</Pill>
                  <span className="ml-auto text-[10.5px] text-v2-ink-muted">
                    {fwRows[0]?.frameworkName}
                  </span>
                </button>
                {!collapsed && (
                  <ul className="divide-y divide-v2-border-subtle">
                    {fwRows.map((r) => (
                      <ControlRow
                        key={rowKey(r)}
                        row={r}
                        expanded={expandedRowKey === rowKey(r)}
                        onToggle={() => toggleExpand(r)}
                      />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── per-row sub-component ──────────────────────────────────

function ControlRow({ row, expanded, onToggle }) {
  const statusTone =
    row.currentStatus === 'ACTIVE'           ? 'success' :
    row.currentStatus === 'TESTING'          ? 'warning' :
    row.currentStatus === 'FAILED'           ? 'danger'  :
    row.currentStatus === 'ARCHIVED'         ? 'muted'   :
                                               'cyan';
  const severityTone =
    row.severity === 'BLOCK' ? 'danger' :
    row.severity === 'WARN'  ? 'warning' :
                               'muted';
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-baseline gap-3 px-3 py-1.5 text-left hover:bg-v2-surface-muted/30 transition-colors"
      >
        {expanded ? <ChevronDown size={11} className="text-v2-ink-muted shrink-0" />
                  : <ChevronRight size={11} className="text-v2-ink-muted shrink-0" />}
        <code className="font-mono-v2 text-[11.5px] font-semibold text-v2-ink shrink-0 min-w-[120px]">
          {row.controlCode}
        </code>
        <span className="text-[12px] text-v2-ink truncate flex-1">
          {row.controlName || <em className="not-italic text-v2-ink-muted">(no name)</em>}
        </span>
        <Pill tone={statusTone} size="sm" dot>{row.currentStatus}</Pill>
        {row.severity && (
          <Pill tone={severityTone} size="sm">{row.severity}</Pill>
        )}
        <span className="text-[10.5px] text-v2-ink-muted font-mono-v2 shrink-0"
              title={row.lastTransitionAt}>
          {row.lastTransitionAt
            ? new Date(row.lastTransitionAt).toLocaleDateString()
            : <em className="not-italic">no transitions in window</em>}
        </span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-v2-border-subtle bg-v2-surface-muted/20">
          <div className="grid gap-2 text-[11px]" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div>
              <div className="text-v2-ink-muted text-[10px] uppercase tracking-wider font-semibold mb-0.5">
                Binding
              </div>
              <code className="font-mono-v2 text-v2-ink-secondary break-all">{row.bindingId}</code>
            </div>
            <div>
              <div className="text-v2-ink-muted text-[10px] uppercase tracking-wider font-semibold mb-0.5">
                Agent
              </div>
              <code className="font-mono-v2 text-v2-ink-secondary break-all">{row.agentId || '—'}</code>
            </div>
            <div>
              <div className="text-v2-ink-muted text-[10px] uppercase tracking-wider font-semibold mb-0.5">
                Mapping source
              </div>
              <span className="text-v2-ink">{row.mappingSource || '—'}</span>
              {row.mappingConfidence != null && (
                <span className="ml-2 text-v2-ink-muted">
                  · confidence {(row.mappingConfidence * 100).toFixed(0)}%
                </span>
              )}
            </div>
            <div>
              <div className="text-v2-ink-muted text-[10px] uppercase tracking-wider font-semibold mb-0.5">
                Transitions in window
              </div>
              <span className="text-v2-ink">{row.transitionsInWindow ?? 0}</span>
            </div>
          </div>

          {row.transitions && row.transitions.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">
                <History size={11} /> Transition history
              </div>
              <ol className="space-y-1">
                {row.transitions.map((t) => (
                  <li key={t.transitionId}
                      className="flex items-baseline gap-2 text-[11px] text-v2-ink">
                    <code className="font-mono-v2 text-v2-ink-muted shrink-0">
                      {t.fromStatus || <em className="not-italic">∅</em>}
                    </code>
                    <span className="text-v2-ink-muted shrink-0">→</span>
                    <code className="font-mono-v2 font-semibold shrink-0">{t.toStatus}</code>
                    {t.reason && (
                      <span className="text-v2-ink-secondary truncate flex-1">{t.reason}</span>
                    )}
                    <span className="ml-auto text-v2-ink-muted font-mono-v2 shrink-0 text-[10.5px]"
                          title={t.transitionedAt}>
                      {t.transitionedAt
                        ? new Date(t.transitionedAt).toLocaleString()
                        : '—'}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export default ControlEvidencePanel;
